import { useEffect, useEffectEvent, useRef } from 'react';

import { useLatestValue, useScrollableContainer } from './index.js';

type InfiniteScrollParams = {
  hasNextPage?: boolean;
  fetchNextPage: () => Promise<unknown>;
  threshold: number;
};

export const useInfiniteScroll = (params: InfiniteScrollParams) => {
  const latestParams = useLatestValue(params);

  const fetching = useRef(false);

  const handleScroll = useEffectEvent(async (event: Event) => {
    const targetElement = event.target;

    if (targetElement) {
      const { hasNextPage, fetchNextPage, threshold } = latestParams.current;

      const { scrollTop, scrollHeight, clientHeight } =
        targetElement as HTMLElement;

      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (hasNextPage && !fetching.current && distanceFromBottom < threshold) {
        fetching.current = true;
        await fetchNextPage();
        fetching.current = false;
      }
    }
  });

  const scrollableContainer = useScrollableContainer();

  useEffect(() => {
    scrollableContainer?.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      scrollableContainer?.removeEventListener('scroll', handleScroll);
    };
  }, [scrollableContainer]);

  return scrollableContainer;
};
